import { useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  ChartLineUp, Bank, ListChecks, Storefront, Receipt, IdentificationCard, Gear,
  SignOut, List as MenuIcon, X, TrendUp,
} from "@phosphor-icons/react";
import { ROLE_LABEL } from "@/lib/format";

const NAV = [
  { to: "/reports", label: "Reports & Analytics", icon: ChartLineUp, show: (a) => a.isAdmin() || a.can("reports.view") },
  { to: "/add-income", label: "Add Income", icon: TrendUp, show: (a) => a.isAdmin() || a.can("reports.view") },
  { to: "/disbursements", label: "Disbursement", icon: Bank, show: (a) => a.can("loan.view") },
  { to: "/all-list", label: "All List", icon: ListChecks, show: (a) => a.isAdmin() || a.canAny("products.add", "lenders.add", "agents.add", "partners.add") },
  { to: "/ob-sales", label: "Own Brand Sales", icon: Storefront, show: (a) => a.can("sales.view") },
  { to: "/expenses", label: "Expenses", icon: Receipt, show: (a) => a.can("expenses.view") },
  { to: "/hr", label: "HR / Documents", icon: IdentificationCard, show: (a) => a.isAdmin() || a.can("users.manage") },
  { to: "/settings", label: "Settings", icon: Gear, show: (a) => a.isAdmin() || a.can("settings.manage") },
];

export default function Layout({ children }) {
  const auth = useAuth();
  const { user, logout } = auth;
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const items = NAV.filter((n) => n.show(auth));

  return (
    <div className="h-screen bg-zinc-50 flex overflow-hidden">
      {/* Sidebar */}
      <aside className={`fixed lg:static z-40 h-screen w-64 shrink-0 bg-brand-dark text-indigo-100 flex flex-col transition-transform ${open ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}>
        <div className="h-16 flex items-center px-4 border-b border-white/10">
          <div className="bg-white rounded-md px-2.5 py-1.5 flex items-center">
            <img src="/paisapaid-logo.webp" alt="PaisaPaid Business ERP" className="h-7 w-auto" />
          </div>
        </div>
        <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-1">
          {items.map((n) => (
            <NavLink key={n.to} to={n.to} onClick={() => setOpen(false)} data-testid={`nav-${n.to.slice(1)}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm transition-colors ${isActive ? "bg-white text-brand font-semibold" : "text-indigo-200 hover:bg-white/10 hover:text-white"}`}>
              <n.icon size={18} weight="duotone" /> {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-white/10">
          <div className="px-3 py-2">
            <div className="text-sm text-white truncate">{user?.name}</div>
            <div className="text-[11px] text-indigo-300 truncate">{ROLE_LABEL(user?.role)}{user?.designation ? ` · ${user.designation}` : ""}</div>
          </div>
          <button data-testid="logout-btn" onClick={logout}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-sm text-sm text-indigo-200 hover:bg-white/10 hover:text-white transition-colors">
            <SignOut size={16} /> Sign out
          </button>
        </div>
      </aside>

      {open && <div className="fixed inset-0 bg-black/40 z-30 lg:hidden" onClick={() => setOpen(false)} />}

      {/* Main */}
      <div className="flex-1 min-w-0 flex flex-col h-screen">
        <header className="h-16 bg-white border-b border-zinc-200 flex items-center justify-between px-5 lg:px-8 shrink-0 z-20">
          <button className="lg:hidden text-zinc-700" onClick={() => setOpen(!open)}>{open ? <X size={22} /> : <MenuIcon size={22} />}</button>
          <div className="hidden lg:block font-heading text-base text-zinc-900 tracking-tight">
            {items.find((n) => location.pathname.startsWith(n.to))?.label || "PaisaPaid ERP"}
          </div>
          <div className="flex items-center gap-3">
            <div className="h-8 w-8 grid place-items-center bg-brand text-white rounded-sm text-sm font-medium">
              {(user?.name || "?").charAt(0).toUpperCase()}
            </div>
          </div>
        </header>
        <main className="flex-1 overflow-y-auto">
          <div className="p-5 lg:p-8 max-w-[1500px] w-full mx-auto">{children}</div>
        </main>
      </div>
    </div>
  );
}
